import { useState, useRef, useCallback } from 'react'

interface UseResizableDrawerOptions {
  storageKey: string
  defaultWidth?: number
  minWidth?: number
  maxWidthRatio?: number
}

export function useResizableDrawer({
  storageKey,
  defaultWidth = 700,
  minWidth = 400,
  maxWidthRatio = 0.95,
}: UseResizableDrawerOptions) {
  const [width, setWidth] = useState<number>(() => {
    const saved = localStorage.getItem(storageKey)
    return saved
      ? Math.max(minWidth, Math.min(window.innerWidth * maxWidthRatio, Number(saved)))
      : defaultWidth
  })

  const resizing = useRef(false)
  const startX = useRef(0)
  const startWidth = useRef(0)

  const onResizeStart = useCallback(
    (e: React.MouseEvent) => {
      resizing.current = true
      startX.current = e.clientX
      startWidth.current = width
      document.body.style.userSelect = 'none'
      document.body.style.cursor = 'ew-resize'

      const onMove = (ev: MouseEvent) => {
        if (!resizing.current) return
        const delta = startX.current - ev.clientX
        const next = Math.max(
          minWidth,
          Math.min(window.innerWidth * maxWidthRatio, startWidth.current + delta),
        )
        setWidth(next)
      }

      const onUp = () => {
        resizing.current = false
        document.body.style.userSelect = ''
        document.body.style.cursor = ''
        setWidth((w) => {
          localStorage.setItem(storageKey, String(w))
          return w
        })
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
      }

      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
    },
    [width, minWidth, maxWidthRatio, storageKey],
  )

  return { width, onResizeStart }
}
